import { useEffect } from 'react';

/**
 * useBodyScrollLock – locks the document body scroll while `isLocked` is true.
 *
 * Safe for nested / stacked modals: uses a shared counter on `window` so the
 * body is only unlocked when the LAST modal closes. Also compensates for the
 * vanishing vertical scrollbar width so page content doesn't shift horizontally
 * when a modal opens.
 *
 * Single source of truth for modal scroll-lock behavior across the app. All
 * modals (both `AnimatedModal`-based and hand-rolled `fixed inset-0` modals)
 * should call this hook with their open-state flag.
 *
 * @param {boolean} isLocked – pass the modal's `isOpen` flag.
 */
const BODY_LOCK_KEY = '__animatedModalBodyLock__';

const lockBodyScroll = () => {
  if (typeof document === 'undefined') return;
  const state = window[BODY_LOCK_KEY] || { count: 0 };
  state.count += 1;

  if (state.count === 1) {
    const { body, documentElement } = document;
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;

    state.previousBodyOverflow = body.style.overflow;
    state.previousBodyPaddingRight = body.style.paddingRight;

    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      const currentPaddingRight = parseInt(
        window.getComputedStyle(body).paddingRight || '0',
        10,
      );
      body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }
  }

  window[BODY_LOCK_KEY] = state;
};

const unlockBodyScroll = () => {
  if (typeof document === 'undefined') return;
  const state = window[BODY_LOCK_KEY];
  if (!state || state.count === 0) return;

  state.count -= 1;

  if (state.count === 0) {
    const { body } = document;
    body.style.overflow = state.previousBodyOverflow || '';
    body.style.paddingRight = state.previousBodyPaddingRight || '';
  }

  window[BODY_LOCK_KEY] = state;
};

const useBodyScrollLock = (isLocked) => {
  useEffect(() => {
    if (!isLocked) return undefined;
    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, [isLocked]);
};

export default useBodyScrollLock;
